import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, ChevronDown, Search, Menu, LogOut, Settings, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { db } from '../../lib/db';
import { formatTimeAgo, formatDate } from '../../lib/dateUtils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useNotifications } from '../../hooks/useDatabase';

export default function Navbar({ onMenuClick }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { getNotifications, clearNotifications } = useNotifications();

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) return;
      try {
        const items = await getNotifications(user.id);
        // Sort newest first
        setNotifications(items.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
      } catch (error) {
        console.error('Failed to load notifications', error);
      }
    };
    loadNotifications();
  }, [user, getNotifications]);

  useEffect(() => {
    const loadUpcomingEvents = async () => {
      if (!user?.id) {
        setUpcomingEvents([]);
        return;
      }

      try {
        const [calendarData, appraisals, goals, users] = await Promise.all([
          db.calendarEvents.toArray(),
          db.appraisals.toArray(),
          db.goals.toArray(),
          db.users.toArray()
        ]);

        const role = user.role;
        const relevantUserIds = (() => {
          if (role === 'admin') return users.map(u => u.id);
          if (role === 'supervisor') return [user.id, ...users.filter(u => u.supervisorId === user.id).map(u => u.id)];
          return [user.id];
        })();

        const derivedAppraisalEvents = appraisals
          .filter(a => {
            if (!a.dueDate) return false;
            if (!relevantUserIds.includes(a.userId)) return false;
            if (role === 'supervisor' && a.visibleToSupervisor === false) return false;
            return true;
          })
          .map(a => ({
            id: `appraisal-${a.id}`,
            title: `Appraisal due: ${a.title}`,
            type: 'appraisal',
            startDate: a.dueDate,
            description: 'Appraisal due date'
          }));

        const derivedGoalEvents = goals
          .filter(g => g.dueDate && relevantUserIds.includes(g.userId))
          .map(g => ({
            id: `goal-${g.id}`,
            title: `Goal due: ${g.title}`,
            type: 'goal',
            startDate: g.dueDate,
            description: g.description || 'Goal due date'
          }));

        const relevantCalendarEvents = role === 'admin'
          ? calendarData
          : calendarData.filter(event => {
              const createdByRelevant = event.createdBy && relevantUserIds.includes(event.createdBy);
              const participantOverlap = (event.participants || []).some(pid => relevantUserIds.includes(pid));
              return createdByRelevant || participantOverlap;
            });

        const combinedEvents = [
          ...relevantCalendarEvents,
          ...derivedAppraisalEvents,
          ...derivedGoalEvents
        ];

        const sortedUpcoming = combinedEvents
          .filter(e => e.startDate && new Date(e.startDate).getTime() >= Date.now())
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
          .slice(0, 3);

        setUpcomingEvents(sortedUpcoming);
      } catch (error) {
        console.error('Failed to load calendar events', error);
        setUpcomingEvents([]);
      }
    };

    loadUpcomingEvents();
  }, [user]);

  const userNotifications = notifications;
  const unreadCount = userNotifications.filter(n => !n.read).length;

  const handleViewCalendar = () => {
    if (!user?.role) return;
    setIsCalendarOpen(false);
    navigate(`/${user.role}/calendar`);
  };

  const handleClearNotifications = async () => {
    if (!user?.id) return;
    try {
      await clearNotifications(user.id);
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear notifications', error);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-100 text-rose-700';
      case 'supervisor': return 'bg-violet-100 text-violet-700';
      case 'employee': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-semibold text-gray-900 hidden sm:block">PerformanceHub</span>
          </div>
        </div>

        {/* Search bar */}
        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees, appraisals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="p-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {userNotifications.length > 0 ? (
                  userNotifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 ${!notification.read ? 'bg-teal-50/50' : ''}`}
                    >
                      <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{notification.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatTimeAgo(notification.timestamp)}</p>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                )}
              </div>
              <div className="p-2 border-t border-gray-100">
                <Button
                  variant="ghost"
                  className="w-full text-teal-600 text-sm"
                  onClick={handleClearNotifications}
                  disabled={userNotifications.length === 0}
                >
                  Clear Notifications
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Calendar */}
          <DropdownMenu open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Calendar className="w-5 h-5 text-gray-600" />
                {upcomingEvents.length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <div className="p-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Upcoming Events</h3>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map(event => (
                    <div key={event.id} className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50">
                      <p className="text-sm font-medium text-gray-900">{event.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(event.startDate)}</p>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">No upcoming events</div>
                )}
              </div>
              <div className="p-2 border-t border-gray-100">
                <Button variant="ghost" className="w-full text-teal-600 text-sm" onClick={handleViewCalendar}>View Calendar</Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-3">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.profile?.firstName?.[0]}{user?.profile?.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.profile?.firstName} {user?.profile?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="p-3 border-b border-gray-100">
                <p className="font-medium text-gray-900">
                  {user?.profile?.firstName} {user?.profile?.lastName}
                </p>
                <p className="text-sm text-gray-500">{user?.email}</p>
                <Badge className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </Badge>
              </div>
              <DropdownMenuItem className="gap-2 cursor-pointer">
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer">
                <Settings className="w-4 h-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="gap-2 cursor-pointer text-rose-600 focus:text-rose-600"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
